import { apiClient, type ManualCommandConfig } from '@/services/http/client'

export const policyActions = [
  'profile.read',
  'profile.update',
  'document.read',
  'document.download',
  'document.upload',
  'output.read',
  'output.download',
  'output.publish',
  'firm_access.manage',
  'client_switch.manage',
] as const
export type PolicyAction = 'submit' | 'decide' | 'retire'
export interface PolicyRule {
  rule_id: string
  subject_type: 'business' | 'HUF'
  capacity: string
  controller_role: 'legal_controller' | 'representative'
  allowed_actions: string[]
  required_evidence_types: string[]
  maximum_authority_seconds: number
  independent_acceptance_required: true
}
export interface PolicyCommand {
  command_id: string
  policy_id: string
  action: PolicyAction
  data: Record<string, unknown>
}
export interface PolicyReceipt {
  command_id: string
  policy_id: string
  action: PolicyAction
  command_sha256: string
  policy_body_sha256: string
  actor_user_id: string
  committed_at: string
  request: PolicyCommand
  authority: 'committed_platform_policy_command_only'
}
export interface PolicyReply {
  command_id: string
  policy_id: string
  status: 'projected' | 'pending'
  authority: 'policy_history_only'
  receipt: PolicyReceipt | null
}
const path = '/legal-control-policies/commands'
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
function normalized(command: PolicyCommand): string {
  const data = JSON.parse(JSON.stringify(command.data))
  if (command.action === 'submit') {
    data.effective_at = new Date(data.effective_at).toISOString()
    data.expires_at = new Date(data.expires_at).toISOString()
    data.body.rules = [...data.body.rules]
      .map((rule: PolicyRule) => ({
        ...rule,
        allowed_actions: [...rule.allowed_actions].sort(),
        required_evidence_types: [...rule.required_evidence_types].sort(),
      }))
      .sort((a: PolicyRule, b: PolicyRule) => a.rule_id.localeCompare(b.rule_id))
  }
  const stable = (value: unknown): unknown =>
    Array.isArray(value)
      ? value.map(stable)
      : value && typeof value === 'object'
        ? Object.fromEntries(
            Object.entries(value)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([name, item]) => [name, stable(item)])
          )
        : value
  return JSON.stringify(stable({ ...command, data }))
}
export function policyReply(
  value: unknown,
  id: string,
  policyId?: string,
  expected?: PolicyCommand
): PolicyReply {
  const item = value as PolicyReply
  if (
    !item ||
    item.command_id !== id ||
    !uuid.test(item.policy_id) ||
    (policyId && item.policy_id !== policyId) ||
    item.authority !== 'policy_history_only' ||
    !['pending', 'projected'].includes(item.status)
  )
    throw new Error('Policy outcome could not be confirmed. Keep the original command ID.')
  if (item.status === 'pending' && item.receipt !== null)
    throw new Error('Invalid pending policy outcome')
  if (
    item.status === 'projected' &&
    (!item.receipt ||
      item.receipt.command_id !== id ||
      item.receipt.policy_id !== item.policy_id ||
      item.receipt.request?.command_id !== id ||
      item.receipt.authority !== 'committed_platform_policy_command_only')
  )
    throw new Error('Policy receipt does not match the original command')
  if (item.receipt) {
    const receipt = item.receipt
    if (
      !['submit', 'decide', 'retire'].includes(receipt.action) ||
      receipt.request.action !== receipt.action ||
      receipt.request.policy_id !== item.policy_id ||
      !receipt.request.data ||
      typeof receipt.request.data !== 'object' ||
      !/^[0-9a-f]{64}$/.test(receipt.policy_body_sha256) ||
      !uuid.test(receipt.actor_user_id) ||
      !Number.isFinite(Date.parse(receipt.committed_at))
    )
      throw new Error('Unsupported policy receipt')
    if (expected && normalized(receipt.request) !== normalized(expected))
      throw new Error('Policy receipt content does not match the prepared command')
  }
  return item
}
export async function sendPolicyCommand(command: PolicyCommand): Promise<PolicyReply> {
  const config: ManualCommandConfig = { manualCommand: true }
  const snapshot: PolicyCommand = JSON.parse(JSON.stringify(command))
  const response = await apiClient.post(path, snapshot, config)
  return policyReply(response.data, snapshot.command_id, snapshot.policy_id, snapshot)
}
export async function recoverPolicyCommand(id: string): Promise<PolicyReply> {
  if (!uuid.test(id)) throw new Error('Enter a valid command ID')
  return policyReply((await apiClient.get(`${path}/${id}`)).data, id)
}
export interface PolicyHistory {
  commandId: string
  policyId: string
  action: PolicyAction
  status: 'unknown' | 'pending' | 'projected'
}
const key = (user: string) => `taxhive:policy-command-ids:${user}`
export function readPolicyHistory(user: string): PolicyHistory[] {
  try {
    const items: unknown = JSON.parse(localStorage.getItem(key(user)) ?? '[]')
    if (!Array.isArray(items)) return []
    return items
      .filter(
        (v): v is PolicyHistory =>
          v &&
          uuid.test(v.commandId) &&
          uuid.test(v.policyId) &&
          ['submit', 'decide', 'retire'].includes(v.action) &&
          ['unknown', 'pending', 'projected'].includes(v.status)
      )
      .map(v => ({
        commandId: v.commandId,
        policyId: v.policyId,
        action: v.action,
        status: v.status,
      }))
  } catch {
    return []
  }
}
export function rememberPolicyCommand(
  user: string,
  command: PolicyCommand,
  status: PolicyHistory['status']
): PolicyHistory[] {
  const items = readPolicyHistory(user)
  const item = {
    commandId: command.command_id,
    policyId: command.policy_id,
    action: command.action,
    status,
  }
  const result = [item, ...items.filter(v => v.commandId !== item.commandId)]
  localStorage.setItem(key(user), JSON.stringify(result))
  return result
}

export interface PendingPolicyCursor {
  recorded_at: string
  command_id: string
}
export interface PendingPolicyItem extends PendingPolicyCursor {
  policy_id: string
  action: PolicyAction
}
export interface PendingPolicyPage {
  items: PendingPolicyItem[]
  next_cursor: PendingPolicyCursor | null
  authority: 'policy_pending_inventory_only'
  view: 'live_refresh_first_page_after_changes'
}
export async function pendingPolicyCommands(
  after?: PendingPolicyCursor
): Promise<PendingPolicyPage> {
  const params: Record<string, string | number> = { limit: 50 }
  if (after) {
    params.after_recorded_at = after.recorded_at
    params.after_command_id = after.command_id
  }
  const { data } = await apiClient.get<PendingPolicyPage>(`${path}/pending`, { params })
  const validCursor = (v: PendingPolicyCursor) =>
    v &&
    typeof v.recorded_at === 'string' &&
    Number.isFinite(Date.parse(v.recorded_at)) &&
    typeof v.command_id === 'string' &&
    uuid.test(v.command_id)
  if (
    !data ||
    data.authority !== 'policy_pending_inventory_only' ||
    data.view !== 'live_refresh_first_page_after_changes' ||
    !Array.isArray(data.items) ||
    data.items.length > 50 ||
    new Set(data.items.map(v => v.command_id)).size !== data.items.length ||
    data.items.some(
      v =>
        !validCursor(v) ||
        !uuid.test(v.policy_id) ||
        !['submit', 'decide', 'retire'].includes(v.action) ||
        Object.keys(v).sort().join(',') !== 'action,command_id,policy_id,recorded_at'
    ) ||
    (data.next_cursor !== null &&
      (!validCursor(data.next_cursor) ||
        data.items.length !== 50 ||
        data.next_cursor.command_id !== data.items[data.items.length - 1].command_id ||
        data.next_cursor.recorded_at !== data.items[data.items.length - 1].recorded_at))
  )
    throw new Error('Exact pending inventory unavailable')
  return data
}

export interface CurrentPolicyObservation {
  policy_id: string
  policy_key: string
  version: number
  body_sha256: string
  body: { schema_version: 1; rules: PolicyRule[] }
  effective_at: string
  expires_at: string
  checked_at: string
  authority: 'source_policy_observation_only'
}
export async function inspectCurrentPolicy(
  policyId: string,
  bodyHash: string
): Promise<CurrentPolicyObservation> {
  const id = policyId.trim().toLowerCase()
  const hash = bodyHash.trim()
  if (!uuid.test(id) || !/^[0-9a-f]{64}$/.test(hash))
    throw new Error('Exact policy identity required')
  const { data } = await apiClient.get<CurrentPolicyObservation>(
    `/legal-control-policies/${id}/current`,
    { params: { body_sha256: hash } }
  )
  const identifier = /^[A-Za-z][A-Za-z0-9._-]{0,79}$/
  const validRule = (r: PolicyRule) =>
    r &&
    typeof r.rule_id === 'string' &&
    typeof r.capacity === 'string' &&
    identifier.test(r.rule_id) &&
    identifier.test(r.capacity) &&
    ['business', 'HUF'].includes(r.subject_type) &&
    ['legal_controller', 'representative'].includes(r.controller_role) &&
    r.independent_acceptance_required === true &&
    Number.isInteger(r.maximum_authority_seconds) &&
    r.maximum_authority_seconds >= 1 &&
    r.maximum_authority_seconds <= 2147483647 &&
    Array.isArray(r.allowed_actions) &&
    r.allowed_actions.length >= 1 &&
    r.allowed_actions.length <= 10 &&
    new Set(r.allowed_actions).size === r.allowed_actions.length &&
    r.allowed_actions.every(v => (policyActions as readonly string[]).includes(v)) &&
    Array.isArray(r.required_evidence_types) &&
    r.required_evidence_types.length >= 1 &&
    r.required_evidence_types.length <= 10 &&
    new Set(r.required_evidence_types).size === r.required_evidence_types.length &&
    r.required_evidence_types.every(v => typeof v === 'string' && identifier.test(v))
  const now = Date.now()
  const checked = Date.parse(data?.checked_at)
  const expiry = Date.parse(data?.expires_at)
  const effective = Date.parse(data?.effective_at)
  const rules = data?.body?.rules
  if (
    !data ||
    data.authority !== 'source_policy_observation_only' ||
    data.policy_id !== id ||
    data.body_sha256 !== hash ||
    typeof data.policy_key !== 'string' ||
    !identifier.test(data.policy_key) ||
    !Number.isInteger(data.version) ||
    data.version < 1 ||
    data.version > 2147483647 ||
    !Number.isFinite(checked) ||
    !Number.isFinite(expiry) ||
    !Number.isFinite(effective) ||
    effective > checked ||
    checked > now ||
    now - checked > 30000 ||
    expiry <= now ||
    data.body?.schema_version !== 1 ||
    !Array.isArray(rules) ||
    rules.length < 1 ||
    rules.length > 32 ||
    !rules.every(validRule) ||
    new Set(rules.map(r => r.rule_id)).size !== rules.length ||
    new Set(rules.map(r => `${r.subject_type}:${r.capacity}:${r.controller_role}`)).size !==
      rules.length
  )
    throw new Error('Current policy observation unavailable')
  return data
}
